import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useDash } from "@/pages/dashboard/ctx";
import { toast } from "sonner";
import { Palette, ImagePlus, Save, Loader2, Sun, Moon, Building2 } from "lucide-react";
import { BrandFont, loadFontStylesheet } from "@/components/BrandFont";
import { V } from "@/lib/vocab";
import { SettingsSection } from "@/components/SettingsSection";

const BRAND_PRESETS = [
  ["#D32F2F", "Redline red"], ["#1B7F4B", "Forest green"], ["#1D5FBF", "Lake blue"],
  ["#0D9488", "Teal"], ["#7C3AED", "Violet"], ["#B45309", "Amber"], ["#BE185D", "Berry"],
];
const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

export const brandLogoUrl = (tenantId, ver) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public/tenant/${tenantId}/logo?v=${ver || ""}`;

// One logo, one colour, one font for every surface — lives on Settings → Channels & Experience.
export function BrandCard() {
  const { tenantId, role } = useDash();
  const readOnly = role === "board_member";
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/community-profile/brand`)
      .then((r) => {
        setData(r.data);
        setForm({ brand_color: r.data.brand_color || "", brand_font: r.data.brand_font || "default", logo_path: r.data.logo_path || "", sidebar_theme: ["inherit", "light", "dark"].includes(r.data.sidebar_theme) ? r.data.sidebar_theme : "inherit" });
        setDirty(false);
      })
      .catch(() => toast.error("Couldn't load the brand settings"));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (data?.font_options || []).forEach((f) => loadFontStylesheet(f.google)); }, [data?.font_options]);

  const upd = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/community-profile/brand`, form);
      setData((d) => ({ ...d, ...r.data }));
      setDirty(false);
      toast.success("Brand saved");
      window.dispatchEvent(new CustomEvent("askredline:brand-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Keep the logo under 4 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("logo_path", r.data.path);
      toast.success("Logo uploaded — hit Save to use it everywhere");
    } catch { toast.error("Upload failed"); }
  };

  if (!data || !form) {
    return <div className="rounded-2xl border border-sand-200 bg-white p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="brand-card-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading brand…</div>;
  }

  const color = form.brand_color || "#D32F2F";
  const font = (data.font_options || []).find((f) => f.key === (form.brand_font || "default"));

  return (
    <SettingsSection id="brand-card" icon={Palette} title="Brand"
      badges={[dirty && { text: "unsaved", warn: true }]}
      summary={`${form.logo_path ? "Logo set" : "No logo yet"} · colour ${color} · ${font?.label || "default font"} — used on every ${V.lc("person")}-facing surface.`}
      headerRight={!readOnly ? (
        <button data-testid="brand-save-btn" disabled={saving || !dirty} onClick={save}
          className={`shrink-0 rounded-full font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-60 ${dirty ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-ink text-white"}`}>
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      ) : null}>
      <p className="text-sm text-muted-foreground -mt-1">One logo, one colour and one font for every {V.lc("person")}-facing surface — public page, invites, emails, the {V.grp()} report letterhead and this console.</p>

      <div className="flex flex-wrap items-center gap-3">
        {BRAND_PRESETS.map(([hex, label]) => {
          const active = color.toLowerCase() === hex.toLowerCase();
          return (
            <button key={hex} data-testid={`brand-color-${hex.slice(1)}`} disabled={readOnly} title={label}
              onClick={() => upd("brand_color", hex === "#D32F2F" ? "" : hex)}
              className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-ink scale-110" : "border-transparent"}`}
              style={{ backgroundColor: hex }} />
          );
        })}
        <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          Custom
          <input type="color" data-testid="brand-color-custom" disabled={readOnly} value={color}
            onChange={(e) => upd("brand_color", e.target.value)} className="h-5 w-7 cursor-pointer border-0 bg-transparent p-0" />
        </label>
        {form.brand_color && !readOnly && (
          <button data-testid="brand-color-reset" onClick={() => upd("brand_color", "")}
            className="text-xs font-bold text-muted-foreground hover:text-redline-600">Reset to Redline red</button>
        )}
        <span className="rounded-full text-white text-xs font-bold px-4 py-1.5" style={{ backgroundColor: color }}
          data-testid="brand-color-preview">Text the concierge</span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0">
          <label className="text-sm font-semibold">Sidebar theme <span className="font-normal text-muted-foreground">— this {V.lc("org")}'s console</span></label>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">Light or dark sidebar while you're inside this {V.lc("org")}. Keep <span className="font-semibold text-ink">Inherit</span> to follow your management firm's default.</p>
        </div>
        <div className="flex gap-0.5 rounded-full border border-sand-200 bg-white p-0.5 shrink-0" data-testid="brand-sidebar-theme">
          {[["inherit", "Inherit", Building2], ["light", "Light", Sun], ["dark", "Dark", Moon]].map(([val, lbl, Icon]) => {
            const active = (form.sidebar_theme || "inherit") === val;
            return (
              <button key={val} data-testid={`brand-sidebar-theme-${val}`} disabled={readOnly}
                onClick={() => upd("sidebar_theme", val)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                <Icon className="h-3.5 w-3.5" /> {lbl}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full md:w-80">
          <label className="text-sm font-semibold">{V.one("org")} font <span className="font-normal text-muted-foreground">— what {V.lcs("person")} see</span></label>
          <select data-testid="brand-font" disabled={readOnly} value={form.brand_font || "default"}
            onChange={(e) => upd("brand_font", e.target.value)} className={`${inputCls} mt-1`}>
            {(data.font_options || []).map((f) => (
              <option key={f.key} value={f.key}>{f.label} — {f.blurb}</option>
            ))}
          </select>
        </div>
        {font && (
          <div className="rounded-xl border border-sand-200 bg-sand-50 px-4 py-3 min-w-0 flex-1" data-testid="brand-font-preview">
            <BrandFont brand={{ font_css: font.google, font_heading: font.heading, font_body: font.body }} scope="[data-brandfont-preview]" />
            <div data-brandfont-preview>
              <div className="font-heading font-bold text-ink text-lg" style={{ fontFamily: font.heading }} data-testid="brand-font-preview-heading">
                {data.name || `Your ${V.lc("org")}`}
              </div>
              <div className="text-sm text-ink/70" style={{ fontFamily: font.body }} data-testid="brand-font-preview-body">
                The pool is open 6am to 10pm. Quiet hours start at 10pm — text us anytime.
              </div>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Your font applies to the {V.lc("person")} portal, your public {V.lc("org")} page, your sign-in pages and
        opt-in links — and this console wears it while you're inside the {V.lc("org")}, once you save. Emails and the
        PDF {V.grp()} report use the closest match that survives email clients — those don't load web fonts.
      </p>

      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-sand-200">
        {form.logo_path ? (
          <>
            {form.logo_path === data.logo_path && data.has_logo && (
              <img src={brandLogoUrl(tenantId, data.logo_ver)} alt="" data-testid="brand-logo-thumb"
                className="h-10 max-w-[7rem] object-contain rounded-lg border border-sand-200 bg-white p-1" />
            )}
            <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5 font-bold" data-testid="brand-logo-set">Logo set ✓</span>
          </>
        ) : <span className="text-xs text-muted-foreground" data-testid="brand-logo-none">No logo yet — surfaces show your {V.lc("org")} name alone.</span>}
        {!readOnly && (
          <>
            <button data-testid="brand-upload-logo" onClick={() => logoRef.current?.click()}
              className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5">
              <ImagePlus className="h-4 w-4" /> {form.logo_path ? "Replace logo" : "Upload logo"}
            </button>
            {form.logo_path && (
              <button data-testid="brand-remove-logo" onClick={() => upd("logo_path", "")}
                className="text-xs font-bold text-muted-foreground hover:text-danger-600">Remove</button>
            )}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
          </>
        )}
      </div>
    </SettingsSection>
  );
}
